/* BB CUSTOM JS */

// BBCustomBanner element for scroll navigation
class BBCustomBanner extends HTMLElement {
  constructor() {
    super();
    this.scrollContainer = null;
    this.scrollButton = null;
    this.abortController = null;
  }

  connectedCallback() {
    this.abortController = new AbortController();

    // Find elements within the wrapper
    const wrapper = this.querySelector('.bb-custom-banner__scroll-wrapper');
    if (!wrapper) return;

    this.scrollContainer = wrapper.querySelector('.custom-quick-navigation');
    this.scrollButton = wrapper.querySelector('.bb-custom-banner__scroll-button');

    if (!this.scrollContainer || !this.scrollButton) return;

    this.setupEventListeners();

    // Check overflow after a small delay to ensure all elements are loaded
    setTimeout(() => {
      this.checkOverflow();
    }, 100);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  setupEventListeners() {
    this.scrollButton.addEventListener('click', this.scrollToNext.bind(this));

    this.scrollContainer.addEventListener('scroll', this.onScroll.bind(this), {
      passive: true,
      signal: this.abortController.signal,
    });

    window.addEventListener('resize', this.checkOverflow.bind(this), {
      signal: this.abortController.signal,
    });
  }

  checkOverflow() {
    if (!this.scrollContainer) return;

    const hasOverflow = this.scrollContainer.scrollWidth > this.scrollContainer.clientWidth;
    this.updateButtonVisibility(hasOverflow);
  }

  updateButtonVisibility(show) {
    if (!this.scrollButton) return;

    if (show) {
      this.scrollButton.style.display = 'flex';
      this.onScroll(); // Check if at end
    } else {
      this.scrollButton.style.display = 'none';
    }
  }

  scrollToNext() {
    if (!this.scrollContainer) return;

    // Calculate distance based on container width for responsive scrolling
    const containerWidth = this.scrollContainer.clientWidth;
    const scrollDistance = Math.min(containerWidth * 0.8, 200); // Scroll 80% of container width or 200px max

    const currentScroll = this.scrollContainer.scrollLeft;
    const targetScroll = currentScroll + scrollDistance;

    this.scrollContainer.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  }

  onScroll() {
    if (!this.scrollContainer || !this.scrollButton) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5; // 5px tolerance

    this.scrollButton.style.display = isAtEnd ? 'none' : 'flex';
  }
}

if (!window.customElements.get('bb-custom-banner')) {
  window.customElements.define('bb-custom-banner', BBCustomBanner);
}

// BBCustomMultiColumn element for scroll navigation
class BBCustomMultiColumn extends HTMLElement {
  constructor() {
    super();
    this.scrollContainer = null;
    this.scrollButton = null;
    this.abortController = null;
  }

  connectedCallback() {
    this.abortController = new AbortController();

    // Find elements within the wrapper
    const wrapper = this.querySelector('.bb-custom-multi-column__scroll-wrapper');
    if (!wrapper) return;

    this.scrollContainer = wrapper.querySelector('.multi-column');
    this.scrollButton = wrapper.querySelector('.bb-custom-multi-column__scroll-button');

    if (!this.scrollContainer || !this.scrollButton) return;

    this.setupEventListeners();

    // Check overflow after a small delay to ensure all elements are loaded
    setTimeout(() => {
      this.checkOverflow();
    }, 100);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  setupEventListeners() {
    this.scrollButton.addEventListener('click', this.scrollToNext.bind(this));

    this.scrollContainer.addEventListener('scroll', this.onScroll.bind(this), {
      passive: true,
      signal: this.abortController.signal,
    });

    window.addEventListener('resize', this.checkOverflow.bind(this), {
      signal: this.abortController.signal,
    });
  }

  checkOverflow() {
    if (!this.scrollContainer) return;

    const hasOverflow = this.scrollContainer.scrollWidth > this.scrollContainer.clientWidth;
    this.updateButtonVisibility(hasOverflow);
  }

  updateButtonVisibility(show) {
    if (!this.scrollButton) return;

    if (show) {
      this.scrollButton.style.display = 'flex';
      this.onScroll(); // Check if at end
    } else {
      this.scrollButton.style.display = 'none';
    }
  }

  scrollToNext() {
    if (!this.scrollContainer) return;

    // Get the first column item to calculate scroll distance
    const firstColumn = this.scrollContainer.querySelector('.multi-column__item');
    if (!firstColumn) return;

    // Get the computed styles to access CSS custom properties
    const containerStyles = getComputedStyle(this.scrollContainer);
    const columnGap = parseFloat(containerStyles.getPropertyValue('--multi-column-column-gap')) || 0;

    // Calculate column width including gap
    const columnRect = firstColumn.getBoundingClientRect();
    const scrollDistance = columnRect.width + columnGap;

    const currentScroll = this.scrollContainer.scrollLeft;
    const targetScroll = currentScroll + scrollDistance;

    this.scrollContainer.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  }

  onScroll() {
    if (!this.scrollContainer || !this.scrollButton) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5; // 5px tolerance

    this.scrollButton.style.display = isAtEnd ? 'none' : 'flex';
  }
}

if (!window.customElements.get('bb-custom-multi-column')) {
  window.customElements.define('bb-custom-multi-column', BBCustomMultiColumn);
}

/* WISHLIST */

export class BBWishlist {
  async getList() {
    const localStorageValue = localStorage.getItem('bb-wishlist');
    if (localStorageValue == null) return [];
    let parsedValue;
    try {
      parsedValue = JSON.parse(localStorageValue);
    } catch (e) {
      return [];
    }
    return parsedValue;
  }

  setList(handles) {
    localStorage.setItem('bb-wishlist', JSON.stringify(handles ?? []));
  }

  async hasEntry(handle) {
    const list = await this.getList();
    return list.some((h) => h === handle);
  }

  async removeEntry(handle) {
    const list = await this.getList();
    this.setList(list.filter((h) => h !== handle));
  }

  async addEntry(handle) {
    const list = await this.getList();
    if (list.some((h) => h === handle)) return;
    list.push(handle);
    this.setList(list);
  }

  clearList() {
    this.setList();
  }
}
export class BBWishlistGrid extends HTMLElement {
  constructor() {
    super();
    this.wishlist = new BBWishlist();
    this.grid = null;
  }

  async connectedCallback() {
    // Initialize grid reference
    this.grid = this.querySelector('[data-grid]');

    if (!this.grid) {
      console.error('BBWishlistGrid: data-grid element not found');
      return;
    }

    // Load wishlist based on URL or local storage
    if (window.location.hash.includes('#wishlist-')) {
      await this.fetchWishlistFromUrl();
    } else {
      await this.fetchWishlist();
    }

    this.setupShareButton();
  }

  /**
   * Set up the share button functionality
   */
  setupShareButton() {
    const shareButton = this.querySelector('[data-wishlist-share]');
    if (!shareButton) return;

    shareButton.addEventListener('click', async () => {
      try {
        const list = await this.wishlist.getList();
        const encoded = encodeURIComponent(list.join(';'));
        const shareData = {
          text: 'Wunschliste',
          title: 'Wunschliste',
          url: window.location.hash.includes('#wishlist-') ? window.location.href : `${window.location.href}#wishlist-${encoded}`,
        };

        // Try native sharing first, fallback to clipboard
        if (navigator.share) {
          await navigator.share(shareData);
        } else {
          await navigator.clipboard.writeText(shareData.url);
        }
      } catch (error) {
        console.error('Error sharing wishlist:', error);
        // Fallback to clipboard if sharing fails
        try {
          const list = await this.wishlist.getList();
          const encoded = encodeURIComponent(list.join(';'));
          const url = window.location.hash.includes('#wishlist-') ? window.location.href : `${window.location.href}#wishlist-${encoded}`;
          await navigator.clipboard.writeText(url);
        } catch (clipboardError) {
          console.error('Error copying to clipboard:', clipboardError);
        }
      }
    });
  }

  /**
   * Fetch wishlist from local storage and render
   */
  async fetchWishlist() {
    try {
      const list = await this.wishlist.getList();

      const encoded = encodeURIComponent(list.join(';'));
      const url = `/search?view=wishlist&q=${encoded}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      this.grid.innerHTML = html;

      if (list.length > 0) {
        this.setupAddAllButton();
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    }
  }

  /**
   * Fetch wishlist from URL hash and render
   */
  async fetchWishlistFromUrl() {
    try {
      const hash = window.location.hash.replace('#wishlist-', '');

      const url = `/search?view=wishlist&q=${hash}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      this.grid.innerHTML = html;
      this.setupProductInteractions();
    } catch (error) {
      console.error('Error fetching wishlist from URL:', error);
      this.renderErrorState();
    }
  }

  /**
   * Set up add all to cart button
   */
  setupAddAllButton() {
    const addAllButton = this.querySelector('[data-add-all]');
    if (!addAllButton) return;

    addAllButton.addEventListener('click', async () => {
      // Set loading state
      addAllButton.setAttribute('aria-busy', 'true');

      try {
        // Get available product forms (sold out items won't have forms)
        const productForms = this.querySelectorAll('.shopify-product-form');
        const items = [];

        productForms.forEach((form) => {
          const variantIdInput = form.querySelector('input[name="id"]');
          if (variantIdInput && variantIdInput.value) {
            items.push({
              id: variantIdInput.value,
              quantity: 1,
            });
          }
        });

        if (items.length === 0) {
          return;
        }

        // Add items to cart
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseJson = await response.json();

        // Fetch updated cart data and merge sections
        const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
        const cart = await cartResponse.json();
        cart['sections'] = responseJson['sections'];

        // Dispatch cart:change event to trigger cart drawer opening
        document.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              cart: cart,
              baseEvent: 'variant:add',
            },
          }),
        );
      } catch (error) {
        console.error('Error adding items to cart:', error);
      } finally {
        // Remove loading state
        addAllButton.removeAttribute('aria-busy');
      }
    });
  }
}
customElements.define('wishlist-grid', BBWishlistGrid);

export class BBWishlistAdd extends HTMLElement {
  wishlist = new BBWishlist();

  connectedCallback() {
    const button = this.querySelector('button');
    const target = button != null ? button : this;
    target.addEventListener('click', async (evt) => {
      evt.preventDefault();
      const handle = this.dataset.handle;
      if (!handle) {
        console.error('BBWishlistAdd: No handle provided for wishlist entry.');
        return;
      }
      if (await this.wishlist.hasEntry(handle)) {
        await this.wishlist.removeEntry(handle);
        this.removeAttribute('data-wishlist-added');
      } else {
        await this.wishlist.addEntry(handle);
        this.dataset.wishlistAdded = 'true';
      }
    });

    this.wishlist.getList().then((list) => {
      if (list.some((h) => h === this.dataset.handle)) {
        this.dataset.wishlistAdded = 'true';
      } else {
        this.removeAttribute('data-wishlist-added');
      }
    });
  }
}
customElements.define('wishlist-add', BBWishlistAdd);

// Selling Plan Picker custom element
export class BBSellingPlanPicker extends HTMLElement {
  constructor() {
    super();
    this.oneTimeRadio = null;
    this.subscriptionRadio = null;
    this.sellingPlanSelect = null;
    this.productForm = null;
    this.abortController = null;
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.oneTimeRadio = this.querySelector('input[value="one-time"]');
    this.subscriptionRadio = this.querySelector('input[value="subscription"]');
    this.sellingPlanSelect = this.querySelector('.selling-plan-picker__select');

    const formId = this.dataset.formId;
    if (formId) {
      this.productForm = document.getElementById(formId);
    }

    this.setupEventListeners();
    this.updateSelectState();

    // Initialize with default selection if subscription radio is checked
    if (this.subscriptionRadio && this.subscriptionRadio.checked) {
      this.updateProductForm();
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  setupEventListeners() {
    if (this.oneTimeRadio) {
      this.oneTimeRadio.addEventListener('change', this.handleRadioChange.bind(this), {
        signal: this.abortController.signal,
      });
    }

    if (this.subscriptionRadio) {
      this.subscriptionRadio.addEventListener('change', this.handleRadioChange.bind(this), {
        signal: this.abortController.signal,
      });
    }

    if (this.sellingPlanSelect) {
      this.sellingPlanSelect.addEventListener('change', this.handleSelectChange.bind(this), {
        signal: this.abortController.signal,
      });
    }
  }

  handleRadioChange() {
    this.updateSelectState();
    this.updateProductForm();
  }

  handleSelectChange() {
    this.updateProductForm();
  }

  updateSelectState() {
    if (!this.sellingPlanSelect) return;

    if (this.oneTimeRadio && this.oneTimeRadio.checked) {
      this.sellingPlanSelect.disabled = true;
      // Keep the selected value but don't clear it since we pre-select the first option
    } else if (this.subscriptionRadio && this.subscriptionRadio.checked) {
      this.sellingPlanSelect.disabled = false;
      // If no option is selected, select the first one
      if (!this.sellingPlanSelect.value && this.sellingPlanSelect.options.length > 0) {
        this.sellingPlanSelect.selectedIndex = 0;
      }
    }
  }

  updateProductForm() {
    if (!this.productForm) return;

    // Remove existing selling plan input
    const existingInput = this.productForm.querySelector('input[name="selling_plan"]');
    if (existingInput) {
      existingInput.remove();
    }

    // Add new selling plan input if subscription is selected and a plan is available
    if (this.subscriptionRadio && this.subscriptionRadio.checked && this.sellingPlanSelect) {
      // Ensure we have a selected value, if not select the first option
      if (!this.sellingPlanSelect.value && this.sellingPlanSelect.options.length > 0) {
        this.sellingPlanSelect.selectedIndex = 0;
      }

      if (this.sellingPlanSelect.value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        input.value = this.sellingPlanSelect.value;
        this.productForm.appendChild(input);
      }
    }
  }
}
customElements.define('selling-plan-picker', BBSellingPlanPicker);
